// fireEquipmentService.js

export const TYPE = {
  FIRE_BLANKET: 'fire_blanket',
  POWDER_EXT: 'powder_extinguisher',
  FOAM_EXT: 'foam_extinguisher',
  CO2_EXT: 'co2_extinguisher',
  WATER_EXT: 'water_extinguisher',
  FATS_EXT: 'fat_extinguisher',
  HAND_EXT: 'hand_extinguisher',
  AVD_LI_EXT: 'avd_lithium_extinguisher',
  SMOKE_ALARM: 'smoke_alarm',
  EMERGENCY_LIGHT: 'emergency_light',
  EXIT_SIGN: 'exit_sign',
  EVACUATION_PLAN: 'evacuation_plan',
  EXIT_SIGN_PHOTOLUM: 'exit_sign_photoluminescent',
  HOSE_REEL: 'hose_reel',
  HYDRANT: 'hydrant',
  FIRST_AID: 'first_aid',
  HAND_LAMP: 'hand_lamp',
  AED: 'aed',
  SMOKE_HATCH: 'smoke_hatch',
  EYEWASH: 'eyewash'
};

const NAME_TO_CODE = {
  'brandfilt': TYPE.FIRE_BLANKET,
  'pulversläckare': TYPE.POWDER_EXT,
  'skumsläckare': TYPE.FOAM_EXT,
  'skumbrandsläckare': TYPE.FOAM_EXT,
  'kolsyresläckare': TYPE.CO2_EXT,
  'koldioxidsläckare': TYPE.CO2_EXT,
  'vattenbrandsläckare': TYPE.WATER_EXT,
  'fettbrandsläckare': TYPE.FATS_EXT,
  'handbrandsläckare': TYPE.HAND_EXT,
  'avd-litiumbrandsläckare': TYPE.AVD_LI_EXT,
  'avd litiumbrandsläckare': TYPE.AVD_LI_EXT,
  'avd litium brandsläckare': TYPE.AVD_LI_EXT,
  'brandvarnare': TYPE.SMOKE_ALARM,
  'nödbelysning': TYPE.EMERGENCY_LIGHT,
  'hänvisningsarmatur': TYPE.EXIT_SIGN,
  'utrymningsplan': TYPE.EVACUATION_PLAN,
  'hänvisningsarmatur efterlysande': TYPE.EXIT_SIGN_PHOTOLUM,
  'brandpost': TYPE.HYDRANT,
  'första hjälpen': TYPE.FIRST_AID,
  'handlampa': TYPE.HAND_LAMP,
  'hjärtstartare': TYPE.AED,
  'röklucka': TYPE.SMOKE_HATCH,
  'ögondusch': TYPE.EYEWASH
};

// Newer flaw taxonomy codes do not always use the same identifiers as the
// original service-field configuration. Keep the conversion in one place.
const CODE_ALIASES = {
  exit_sign_luminaire: TYPE.EXIT_SIGN,
  photoluminescent_exit_sign: TYPE.EXIT_SIGN_PHOTOLUM,
  emergency_lighting: TYPE.EMERGENCY_LIGHT,
  smoke_detector: TYPE.SMOKE_ALARM,
  fireblanket: TYPE.FIRE_BLANKET,
  grease_extinguisher: TYPE.FATS_EXT,
  hose_reel_cabinet: TYPE.HOSE_REEL,
  fire_hydrant: TYPE.HYDRANT,
  defibrillator: TYPE.AED,
  smoke_vent: TYPE.SMOKE_HATCH,
  eyewash_station: TYPE.EYEWASH,
  flashlight: TYPE.HAND_LAMP
};

function normalizeType(typeOrName) {
  if (typeof typeOrName !== 'string') return '';

  const value = typeOrName.trim();
  const withoutPrefix = value.toLowerCase().startsWith('flaw.category.')
    ? value.slice('flaw.category.'.length)
    : value;

  return withoutPrefix
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, '-')
    .replace(/\s+/g, ' ');
}

const fieldLabels = {
  model: "fire_fields.model",
  type: "fire_fields.type",
  itemClass: "fire_fields.itemClass",
  battery: "fire_fields.battery",
  chargeYear: "fire_fields.chargeYear",
  revisionYear: "fire_fields.revisionYear",
  installationYear: "fire_fields.installationYear",
  comment: "fire_fields.comment",
  reel: "fire_fields.reel",
  feedingTube: "fire_fields.feedingTube",
  electrodes: "fire_fields.electrodes",
  openingType: "fire_fields.openingType",
  fusibleLinkType: "fire_fields.fusibleLinkType",
  fusibleLinkDegrees: "fire_fields.fusibleLinkDegrees",
  gasspringManufacturer: "fire_fields.gasspringManufacturer",
  gasspringModel: "fire_fields.gasspringModel",
  gasspringRevisionYear: "fire_fields.revisionYear", 
  BatteryInstallationYear: "fire_fields.batteryInstallationYear",
  BatteryRevisionYear: "fire_fields.batteryRevisionYear",
  ElectrodesInstallationYear: "fire_fields.electrodesInstallationYear",
  ElectrodesRevisionYear: "fire_fields.electrodesRevisionYear",
  size: "fire_fields.size",
  testPressureYear: "fire_fields.testPressureYear"
};

const fieldMap = {
  [TYPE.EVACUATION_PLAN]: ["installationYear", "comment"],
  [TYPE.FIRE_BLANKET]: ["model", "type", "itemClass", "installationYear", "comment"],
  [TYPE.POWDER_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.HAND_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.FOAM_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.CO2_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.WATER_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.FATS_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.AVD_LI_EXT]: ["model", "type", "itemClass", "chargeYear", "revisionYear", "comment"],
  [TYPE.SMOKE_ALARM]: ["model", "type", "battery", "installationYear", "revisionYear", "comment"],
  [TYPE.HOSE_REEL]: ["model", "type", "reel", "feedingTube", "testPressureYear", "revisionYear", "comment"],
  [TYPE.FIRST_AID]: ["model", "type", "installationYear", "revisionYear", "comment"],
  [TYPE.HAND_LAMP]: ["model", "type", "installationYear", "comment"],
  [TYPE.AED]: [
    "model","type","battery","BatteryInstallationYear","BatteryRevisionYear",
    "electrodes","ElectrodesInstallationYear","ElectrodesRevisionYear","comment"
  ],
  [TYPE.EMERGENCY_LIGHT]: ["model", "type", "battery", "installationYear", "revisionYear", "comment"],
  [TYPE.SMOKE_HATCH]: [
    "model","openingType","fusibleLinkType","fusibleLinkDegrees",
    "gasspringManufacturer","gasspringModel","gasspringRevisionYear","comment"
  ],
  [TYPE.EXIT_SIGN]: ["model", "type", "battery", "installationYear", "revisionYear", "comment"],
  [TYPE.HYDRANT]: ["model", "type", "reel", "feedingTube", "testPressureYear", "revisionYear", "comment"],
  [TYPE.EYEWASH]: ["model", "type", "itemClass", "installationYear", "revisionYear", "comment"],
  [TYPE.EXIT_SIGN_PHOTOLUM]: ["model", "size", "installationYear", "comment"]
};


const EXTINGUISHER_CODES = new Set([
  TYPE.POWDER_EXT, TYPE.FOAM_EXT, TYPE.CO2_EXT, TYPE.WATER_EXT,
  TYPE.FATS_EXT, TYPE.HAND_EXT, TYPE.AVD_LI_EXT
]);

export default {
  getFieldLabels() { return fieldLabels; },
  getFieldMap() { return fieldMap; },
  getAllTypes() { return Object.keys(fieldMap); }, 
  getVisibleFields(typeOrName) {
    const code = this.getTypeCode(typeOrName);
    return fieldMap[code] || [];
  },
  getTypeCode(typeOrName) {
    if (!typeOrName) return '';
    const normalized = normalizeType(typeOrName);
    if (fieldMap[normalized]) return normalized;
    return CODE_ALIASES[normalized] || NAME_TO_CODE[normalized] || '';
  },
  isExtinguisher(typeOrName) {
    const code = this.getTypeCode(typeOrName);
    return EXTINGUISHER_CODES.has(code);
  }
};
